// Authenticated fetch wrapper. Adds the in-memory Bearer token, sends the
// refresh-token cookie via `credentials: "include"`, and on a 401 transparently
// tries `POST /auth/refresh` once and retries the original request. Concurrent
// refresh attempts share a single in-flight promise so a burst of 401s only
// fires one refresh.

import { clearSession, getAccessToken, setAccessToken } from "./store";

const DEFAULT_API_URL = "https://finance-api.ebiz-c90.workers.dev";

export const API_BASE_URL: string = (
  (import.meta.env.VITE_API_URL as string | undefined)?.trim() || DEFAULT_API_URL
).replace(/\/$/, "");

export type AuthFailureReason = "session_expired" | "manual_logout";

type AuthFailureListener = (reason: AuthFailureReason) => void;

const authFailureListeners = new Set<AuthFailureListener>();

// Components (notably the AuthProvider) subscribe so they can navigate to
// /login when a refresh fails or the user logs out. Kept here rather than in
// store.ts so the store stays a pure state holder.
export function onAuthFailure(fn: AuthFailureListener): () => void {
  authFailureListeners.add(fn);
  return () => {
    authFailureListeners.delete(fn);
  };
}

function notifyAuthFailure(reason: AuthFailureReason): void {
  for (const fn of authFailureListeners) fn(reason);
}

let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { accept: "application/json" },
    });
    if (!res.ok) {
      return null;
    }
    const body = (await res.json()) as { access_token?: unknown };
    if (typeof body.access_token !== "string") {
      return null;
    }
    setAccessToken(body.access_token);
    return body.access_token;
  } catch {
    return null;
  }
}

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function buildHeaders(
  init: RequestInit | undefined,
  token: string | null,
): Headers {
  const headers = new Headers(init?.headers ?? {});
  if (!headers.has("accept")) headers.set("accept", "application/json");
  if (init?.body != null && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (token) {
    headers.set("authorization", `Bearer ${token}`);
  } else {
    headers.delete("authorization");
  }
  return headers;
}

interface AuthedFetchOptions extends RequestInit {
  // Skip the implicit 401-retry-with-refresh dance. Used by the login/refresh
  // endpoints themselves so they never recurse.
  skipAuthRetry?: boolean;
}

export async function authedFetch(
  path: string,
  init: AuthedFetchOptions = {},
): Promise<Response> {
  const { skipAuthRetry, ...rest } = init;
  const token = getAccessToken();
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;

  const res = await fetch(url, {
    ...rest,
    credentials: "include",
    headers: buildHeaders(rest, token),
  });

  if (res.status !== 401 || skipAuthRetry) {
    return res;
  }

  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    // Refresh failed → drop any cached user/token and notify subscribers so
    // route guards can bounce to /login.
    clearSession();
    notifyAuthFailure("session_expired");
    return res;
  }

  return fetch(url, {
    ...rest,
    credentials: "include",
    headers: buildHeaders(rest, refreshed),
  });
}

// Logout helper. Best-effort: clears the server-side refresh token + cookie,
// then drops local state. Subscribers (AuthProvider) get notified so the UI
// can route to /login.
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // ignore network errors — local state still needs clearing
  }
  clearSession();
  notifyAuthFailure("manual_logout");
}
